import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getDeviceScreenshots,
  getDeviceScreenshotStats,
  getDeviceScreenshotFacets,
  type DeviceScreenshot,
} from '@/api/device-screenshots';
import { MultiSelect } from '@/components/MultiSelect';
import { Select } from '@/components/Select';
import { COUNTRIES } from '@/lib/countries';
import { formatDateTime } from '@/lib/datetime';
import { ScreenshotImage } from './ScreenshotImage';

const sourceLabels: Record<string, string> = {
  media_observer: 'Media observer',
  file_observer: 'File observer',
  screen_capture_callback: 'Capture callback',
  unknown: 'Unknown',
};

const statusColors: Record<string, string> = {
  active: 'bg-success-soft text-success border-success/30',
  blocked: 'bg-danger-soft text-danger border-danger/30',
};

const reasonLabels: Record<string, string> = {
  none: '-',
  suspicious_apps: 'Suspicious Apps',
  no_sim: 'No SIM',
  geo_mismatch: 'GEO Mismatch',
  review_visit: 'Review / Emulator',
  motion_frozen: 'Frozen sensor',
  tz_mismatch: 'Timezone ≠ IP',
  battery_full: 'Battery 100% charging',
  device_burst: 'Same-model burst',
  bot_fleet: 'Scanner fleet build',
};

const HAS_IMAGE_OPTIONS = [
  { value: 'all', label: 'Image: any' },
  { value: 'yes', label: 'Image: uploaded' },
  { value: 'no', label: 'Image: missing' },
];

const TEST_OPTIONS = [
  { value: 'all', label: 'QA: any' },
  { value: 'exclude', label: 'QA: real only' },
  { value: 'only', label: 'QA: test only' },
];

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Received' },
  { value: 'device_id', label: 'Device ID' },
  { value: 'bundle_id', label: 'Bundle ID' },
  { value: 'source', label: 'Source' },
  { value: 'ip_country', label: 'IP country' },
  { value: 'device_model', label: 'Device model' },
  { value: 'battery_level', label: 'Battery level' },
  { value: 'image_size_bytes', label: 'Image size' },
];

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({
  value: c.code,
  label: `${c.flag} ${c.name} (${c.code})`,
}));

function StatCard({ label, value, color, total }: { label: string; value: number; color: string; total?: number }) {
  const pct = total && total > 0 ? (value / total) * 100 : null;
  const pctText = pct == null ? null : pct >= 10 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5">
      <p className="text-xs font-medium text-content-subtle uppercase tracking-wider">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <p className={`text-2xl font-bold ${color}`}>{value.toLocaleString()}</p>
        {pctText && <span className="text-xs font-medium text-content-subtle">{pctText}</span>}
      </div>
    </div>
  );
}

function formatSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ScreenshotsTab() {
  const [bundleFilter, setBundleFilter] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [hasImage, setHasImage] = useState('all');
  const [test, setTest] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [lightbox, setLightbox] = useState<DeviceScreenshot | null>(null);

  const filterParams = useMemo(() => ({
    bundle: bundleFilter,
    source: sourceFilter,
    country: countryFilter,
    hasImage: hasImage as 'yes' | 'no' | 'all',
    test: test as 'only' | 'exclude' | 'all',
    search,
    dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
    dateTo: dateTo ? new Date(dateTo).toISOString() : undefined,
  }), [bundleFilter, sourceFilter, countryFilter, hasImage, test, search, dateFrom, dateTo]);

  const { data: stats } = useQuery({
    queryKey: ['device-screenshot-stats', filterParams],
    queryFn: () => getDeviceScreenshotStats(filterParams),
    refetchInterval: 30000,
  });

  const { data: facets } = useQuery({
    queryKey: ['device-screenshot-facets'],
    queryFn: getDeviceScreenshotFacets,
    refetchInterval: 60000,
  });

  const bundleOptions = useMemo(
    () => (facets?.bundles || []).map((b) => ({ value: b.value, label: `${b.value} · ${b.count}` })),
    [facets]
  );
  const sourceOptions = useMemo(
    () => (facets?.sources || []).map((s) => ({
      value: s.value,
      label: `${sourceLabels[s.value] || s.value} · ${s.count}`,
    })),
    [facets]
  );

  const queryParams = useMemo(() => ({
    ...filterParams,
    sortBy,
    sortDir,
    page,
    limit: 50,
  }), [filterParams, sortBy, sortDir, page]);

  const { data, isLoading } = useQuery({
    queryKey: ['device-screenshots', queryParams],
    queryFn: () => getDeviceScreenshots(queryParams),
    refetchInterval: 15000,
  });

  const rows = data?.screenshots || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / 50);

  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1); };

  const activeFiltersCount =
    bundleFilter.length + sourceFilter.length + countryFilter.length +
    (hasImage !== 'all' ? 1 : 0) + (test !== 'all' ? 1 : 0) +
    (search ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  function clearAllFilters() {
    setBundleFilter([]);
    setSourceFilter([]);
    setCountryFilter([]);
    setHasImage('all');
    setTest('all');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  }

  function toggleSort(col: string) {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
    setPage(1);
  }

  const COLUMNS: { key: string; label: string; sortKey?: string }[] = [
    { key: 'image', label: 'Shot' },
    { key: 'device_id', label: 'Device ID', sortKey: 'device_id' },
    { key: 'bundle_id', label: 'Bundle ID', sortKey: 'bundle_id' },
    { key: 'source', label: 'Source', sortKey: 'source' },
    { key: 'device_model', label: 'Model / OS', sortKey: 'device_model' },
    { key: 'battery', label: 'Battery', sortKey: 'battery_level' },
    { key: 'ip_country', label: 'IP Country', sortKey: 'ip_country' },
    { key: 'sim', label: 'SIM / GEO' },
    { key: 'sdk_status', label: 'Status' },
    { key: 'block_reason', label: 'Block Reason' },
    { key: 'created_at', label: 'Received', sortKey: 'created_at' },
  ];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border shadow-sm p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search by device ID, bundle ID or screen..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          <MultiSelect
            value={bundleFilter}
            onChange={resetPage(setBundleFilter)}
            options={bundleOptions}
            placeholder="Bundle ID"
            allLabel="All Bundles"
            searchable
            className="min-w-[320px]"
          />
          <MultiSelect
            value={sourceFilter}
            onChange={resetPage(setSourceFilter)}
            options={sourceOptions}
            placeholder="Source"
            allLabel="Any source"
          />
          <MultiSelect
            value={countryFilter}
            onChange={resetPage(setCountryFilter)}
            options={COUNTRY_OPTIONS}
            placeholder="IP country"
            allLabel="Any IP country"
            searchable
          />
          <Select value={hasImage} onChange={resetPage(setHasImage)} options={HAS_IMAGE_OPTIONS} placeholder="Image: any" />
          <Select value={test} onChange={resetPage(setTest)} options={TEST_OPTIONS} placeholder="QA: any" />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
          <span className="text-xs font-medium text-content-subtle uppercase tracking-wider">Received</span>
          <input
            type="datetime-local"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="px-2.5 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
          <span className="text-content-subtle">—</span>
          <input
            type="datetime-local"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="px-2.5 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
          <span className="text-xs font-medium text-content-subtle uppercase tracking-wider ml-4">Sort</span>
          <Select
            value={sortBy}
            onChange={(v) => { setSortBy(v); setPage(1); }}
            options={SORT_OPTIONS}
            placeholder="Sort by"
          />
          <button
            type="button"
            onClick={() => { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); setPage(1); }}
            className="px-3 py-2 text-sm font-medium rounded-lg border border-border bg-surface text-content-muted hover:border-primary/40 hover:bg-primary/10 hover:text-primary transition-colors"
          >
            {sortDir === 'asc' ? 'Asc' : 'Desc'}
          </button>
          {activeFiltersCount > 0 && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="ml-auto text-sm text-primary hover:text-primary font-medium"
            >
              Clear filters ({activeFiltersCount})
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Screenshots" value={stats.total} color="text-content" />
          <StatCard label="With image" value={stats.with_image} color="text-success" total={stats.total} />
          <StatCard label="Unique devices" value={stats.unique_devices} color="text-primary" />
          <StatCard label="Last 24h" value={stats.last_24h} color="text-warning" total={stats.total} />
        </div>
      )}

      {/* Table */}
      <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-content-subtle">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-content-subtle">
            Пока ни одного скриншота — SDK с этим событием ещё не доехал до устройств.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-muted/50">
                  <tr>
                    {COLUMNS.map((col) => {
                      const isActive = col.sortKey && sortBy === col.sortKey;
                      return (
                        <th
                          key={col.key}
                          onClick={() => col.sortKey && toggleSort(col.sortKey)}
                          className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-left select-none ${
                            col.sortKey ? 'cursor-pointer hover:text-primary' : ''
                          } ${isActive ? 'text-primary' : 'text-content-subtle'}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {col.label}
                            {col.sortKey && (
                              <span className="text-[10px]">
                                {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                              </span>
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((s: DeviceScreenshot) => (
                    <>
                      <tr
                        key={s.id}
                        className="transition-colors cursor-pointer hover:bg-surface-muted/50"
                        onClick={() => setExpanded((prev) => {
                          const next = new Set(prev);
                          if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                          return next;
                        })}
                      >
                        <td className="px-4 py-3">
                          {s.has_image ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setLightbox(s); }}
                              className="block w-12 h-20 rounded border border-border overflow-hidden hover:border-primary"
                              title="Open full size"
                            >
                              <ScreenshotImage id={s.id} alt="Screenshot" className="w-full h-full object-cover" />
                            </button>
                          ) : (
                            <span className="inline-flex w-12 h-20 items-center justify-center rounded border border-dashed border-border text-[10px] text-content-subtle text-center leading-tight">
                              no image
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-content-muted max-w-[120px] truncate" title={s.device_id}>
                          {s.device_id.substring(0, 12)}...
                        </td>
                        <td className="px-4 py-3 text-sm text-content-muted whitespace-nowrap" title={s.bundle_id}>
                          {s.bundle_id}
                          {s.is_test === 1 && (
                            <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border bg-surface-muted text-content-subtle border-border">
                              QA
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-content-muted">
                          {sourceLabels[s.source] || s.source}
                          {s.screen && <span className="block text-xs text-content-subtle">{s.screen}</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-content-muted">
                          {s.device_model || '—'}
                          <span className="block text-xs text-content-subtle">{s.os_version || ''}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-content-muted whitespace-nowrap">
                          {s.battery_level == null ? '—' : `${s.battery_level}%`}
                          {s.is_charging != null && (
                            <span className={`block text-xs ${s.is_charging ? 'text-success' : 'text-content-subtle'}`}>
                              {s.is_charging ? 'Charging' : 'On battery'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-content-muted">{s.ip_country || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          {s.sdk_status == null ? (
                            <span className="text-content-subtle">no report</span>
                          ) : (
                            <>
                              <span className={s.has_sim ? 'text-success' : 'text-danger font-medium'}>
                                {s.has_sim ? 'SIM' : 'No SIM'}
                              </span>
                              <span className="text-content-subtle"> / </span>
                              <span className={s.geo_match ? 'text-success' : 'text-danger font-medium'}>
                                {s.geo_match ? 'match' : 'mismatch'}
                              </span>
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {s.sdk_status == null ? (
                            <span className="text-xs text-content-subtle">—</span>
                          ) : (
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[s.sdk_status] || 'bg-surface-muted text-content-muted'}`}>
                              {s.sdk_status}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-content-muted">
                          {s.block_reason ? (reasonLabels[s.block_reason] || s.block_reason) : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-content-subtle whitespace-nowrap">
                          {formatDateTime(s.created_at)}
                        </td>
                      </tr>
                      {expanded.has(s.id) && (
                        <tr key={s.id + '-detail'}>
                          <td colSpan={COLUMNS.length} className="px-4 py-4 bg-surface-muted/80">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-xs text-content-subtle mb-1">Full Device ID</p>
                                <p className="font-mono text-content-muted break-all">{s.device_id}</p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">App / SDK version</p>
                                <p className="text-content-muted">{s.app_version || '—'} / {s.sdk_version || '—'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">Detected at (device clock)</p>
                                <p className="text-content-muted">{s.detected_at || '—'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">Image</p>
                                <p className="text-content-muted">
                                  {s.has_image
                                    ? `${formatSize(s.image_size_bytes)} · ${s.image_content_type} · ${formatDateTime(s.image_uploaded_at)}`
                                    : 'не загружена'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">Battery at screenshot</p>
                                <p className="text-content-muted">
                                  {s.battery_level == null ? '—' : `${s.battery_level}%`}
                                  {s.is_charging != null && ` · ${s.is_charging ? 'charging' : 'on battery'}`}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">Screenshots by this device</p>
                                <p className="text-content-muted">{s.screenshot_count ?? '—'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">SIM country</p>
                                <p className="text-content-muted">{s.sim_country || '—'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">Motion flag</p>
                                <p className="text-content-muted">{s.motion_flag || '—'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">Last ad outcome</p>
                                <p className="text-content-muted">{s.last_ad_outcome || '—'}</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <p className="text-sm text-content-subtle">{total} screenshots total</p>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm rounded border border-border disabled:opacity-40 hover:bg-surface-muted"
                  >
                    Prev
                  </button>
                  <span className="px-3 py-1 text-sm text-content-subtle">{page} / {totalPages}</span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 text-sm rounded border border-border disabled:opacity-40 hover:bg-surface-muted"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <div className="max-h-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <ScreenshotImage
              id={lightbox.id}
              alt={`Screenshot from ${lightbox.device_id}`}
              className="max-h-[80vh] w-auto rounded-lg shadow-xl bg-surface"
            />
            <div className="text-xs text-white/80 text-center">
              <p className="font-mono">{lightbox.device_id}</p>
              <p>
                {lightbox.bundle_id} · {sourceLabels[lightbox.source] || lightbox.source}
                {lightbox.screen ? ` · ${lightbox.screen}` : ''} · {formatDateTime(lightbox.created_at)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="px-4 py-2 text-sm rounded-lg bg-surface text-content border border-border hover:bg-surface-muted"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
